import { useEffect, useState } from 'react';
import useUser from './useUser';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { USER_TYPE } from 'constant/qrcode';
import { useGetUserDataQuery } from 'api/User/queries';
import {
  useStonlyDataQuery,
  useTenantInformationQuery,
} from 'api/Common/queries';
import { getNameInitials } from 'utils/getNameInitials';
import { STATUS_CODES } from 'constant/http';
import { SESSION_STORAGE_KEYS } from 'constant/sessionStorage';
import { emitFatalLog } from 'utils/observability/logger';
import { AnyValueMap } from '@opentelemetry/api-logs';
import { clearStorage } from 'utils/storage/clearStorage';

function useAuth() {
  const { user, setUser, setStonlyData, setTenantInformation } = useUser();
  const token = localStorage.getItem(LOCAL_STORAGE_KEYS.TOKEN);
  const tenant = localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT);
  const userExists = !!user && !!token;
  const isGuestUser =
    (user?.user_type === USER_TYPE.GUEST ||
      user?.user_type === USER_TYPE.NEW_GUEST) ??
    false;

  const isRegisteredVariconUser = user?.user_type === USER_TYPE.VARICON_USER;

  const [isAuthenticated, setIsAuthenticated] = useState(userExists);
  const [loading, setLoading] = useState(!userExists);

  const {
    data: tenantInformation,
    isError: isTenantInformationError,
    error: errorTenantInformation,
  } = useTenantInformationQuery(undefined, {
    enabled: Boolean(token),
  });

  const { data: stonlyData } = useStonlyDataQuery({
    enabled: Boolean(token) && !!tenant,
  });

  const {
    data: userData,
    isLoading: isUserDataLoading,
    isError: isUserDataError,
    error: userDataError,
  } = useGetUserDataQuery({
    enabled: Boolean(token) && !!tenant,
    retry: 2,
  });

  useEffect(() => {
    if (stonlyData) {
      setStonlyData(stonlyData);
    }
  }, [stonlyData, setStonlyData]);

  useEffect(() => {
    if (userData && !isUserDataLoading) {
      const data = {
        ...userData,
        initials: getNameInitials(userData.first_name, userData.last_name),
        id: userData.user_id as string,
      };
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, data?.id);
      sessionStorage.setItem(SESSION_STORAGE_KEYS.EMAIL, data?.email);
      // Guard against absent name fields so sessionStorage never stores "undefined undefined"
      const firstName = userData.first_name ?? '';
      const lastName = userData.last_name ?? '';
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.USER_NAME,
        `${firstName} ${lastName}`.trim() || 'UnknownUser',
      );
      setUser(data);
      setIsAuthenticated(true);
      setLoading(false);
    }
  }, [userData, isUserDataLoading, setUser, setIsAuthenticated, setLoading]);

  useEffect(() => {
    if (!isUserDataLoading && !user && !token) {
      setLoading(false);
    }
  }, [isUserDataLoading, user, token]);

  useEffect(() => {
    if (!tenantInformation) return;
    setTenantInformation(tenantInformation);
    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT)) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.TENANT, tenantInformation[0]?.id);
    }
    // Always refresh tenant_name in sessionStorage — it is cleared on every new
    // tab open so returning users (who already have tenant in localStorage) would
    // otherwise log 'UnknownTenant' for the entire session.
    // Nullish fallback prevents storing the literal string "undefined" when name is absent.
    // getLiveUserAttrs() falls back to 'UnknownTenant' when sessionStorage is empty.
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.TENANT_NAME,
      tenantInformation[0]?.name ?? '',
    );
  }, [tenantInformation, setTenantInformation]);

  useEffect(() => {
    // Bypass the 404 [NotFound] error for QR screens
    const isTenantInformationIssue =
      isTenantInformationError &&
      errorTenantInformation?.status !== STATUS_CODES.NotFound;

    if (isUserDataError || isTenantInformationIssue) {
      clearStorage();
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);

      if (userDataError) {
        emitFatalLog({
          body: {
            message: 'Error fetching user data',
            data: userDataError as unknown as AnyValueMap,
          },
          attrs: {
            source: 'UseAuth',
            level: 'fatal',
            errorSrc: 'USER_DATA_ERROR',
          },
        });
      } else if (errorTenantInformation) {
        emitFatalLog({
          body: {
            message: 'Error fetching tenant information',
            data: errorTenantInformation as unknown as AnyValueMap,
          },
          attrs: {
            source: 'UseAuth',
            errorSrc: 'TENANT_ERROR',
            level: 'fatal',
          },
        });
      }
    }
  }, [
    isUserDataError,
    isTenantInformationError,
    setUser,
    userDataError,
    errorTenantInformation,
  ]);

  return {
    isAuthenticated,
    loading,
    setIsAuthenticated,
    isGuestUser,
    isRegisteredVariconUser,
  };
}

export default useAuth;
